import type {
  ComfyHistory,
  MediaDescriptor,
  ModelLists,
  PromptSubmissionMetadata,
  QueuePromptSummary,
  Workflow,
} from '../types'

type ProgressUpdate = {
  currentNode?: string
  stepValue?: number
  stepMax?: number
}

type QueueResponse = {
  queue_running?: unknown[][]
  queue_pending?: unknown[][]
}
const queuePrompt = (row: unknown[]): QueuePromptSummary | null => {
  const promptId = typeof row?.[1] === 'string' ? row[1] : ''
  if (!promptId) return null
  const extraData = row?.[3]
  const mugen = extraData && typeof extraData === 'object'
    ? (extraData as { mugen?: unknown }).mugen
    : undefined
  const metadata = mugen && typeof mugen === 'object'
    ? mugen as Record<string, unknown>
    : {}
  const summary: QueuePromptSummary = { promptId }
  if (typeof metadata.submittedBy === 'string') summary.submittedBy = metadata.submittedBy
  if (typeof metadata.sourceName === 'string') summary.sourceName = metadata.sourceName
  if (typeof metadata.submittedAt === 'number') summary.submittedAt = metadata.submittedAt
  if (typeof metadata.batchIndex === 'number') summary.batchIndex = metadata.batchIndex
  if (typeof metadata.batchTotal === 'number') summary.batchTotal = metadata.batchTotal
  if (metadata.jobKind === 'generation' || metadata.jobKind === 'enhance' || metadata.jobKind === 'concat') {
    summary.jobKind = metadata.jobKind
  }
  if (typeof metadata.sourceCount === 'number') summary.sourceCount = metadata.sourceCount
  return summary
}

export function formatComfyExecutionError(messages: unknown): string {
  const events = Array.isArray(messages) ? messages : []
  const errors = events
    .filter((event): event is [string, Record<string, unknown>] => (
      Array.isArray(event) && event[0] === 'execution_error' && Boolean(event[1]) && typeof event[1] === 'object'
    ))
    .map((event) => event[1])
  const error = errors.at(-1)
  if (error) {
    const nodeType = typeof error.node_type === 'string' ? error.node_type : 'Unknown node'
    const nodeId = typeof error.node_id === 'string' || typeof error.node_id === 'number' ? ` (#${error.node_id})` : ''
    const exceptionType = typeof error.exception_type === 'string' ? error.exception_type : 'ExecutionError'
    const exceptionMessage = typeof error.exception_message === 'string' ? error.exception_message.trim() : 'Unknown ComfyUI execution error.'
    return `ComfyUI execution failed・${nodeType}${nodeId}\n${exceptionType}: ${exceptionMessage}`
  }
  return `ComfyUI execution failed:\n${JSON.stringify(messages, null, 2)}`
}
const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timer = 0
  const timeout = new Promise<never>((_, reject) => {
    timer = window.setTimeout(() => reject(new Error('Request timed out')), timeoutMs)
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    window.clearTimeout(timer)
  }
}

export class ComfyClient {
  readonly clientId = crypto.randomUUID()
  private serverUrl = ''
  private apiKey = ''
  private socket: WebSocket | null = null
  private reconnectTimer = 0
  private readonly socketListeners = new Map<string, Set<(message: {
    type?: string
    data?: { prompt_id?: string; node?: string | null; value?: number; max?: number }
  }) => void>>()

  configure(serverUrl: string, apiKey = ''): void {
    const nextUrl = serverUrl.trim().replace(/\/+$/, '')
    if (nextUrl !== this.serverUrl) {
      window.clearTimeout(this.reconnectTimer)
      this.socket?.close()
      this.socket = null
    }
    this.serverUrl = nextUrl
    this.apiKey = apiKey.trim()
  }

  private ensureSocket(): void {
    if (!this.serverUrl || this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return
    const wsBase = this.serverUrl.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:')
    let socket: WebSocket
    try {
      socket = new WebSocket(`${wsBase}/ws?clientId=${encodeURIComponent(this.clientId)}`)
    } catch {
      // HTTP polling in monitorPrompt remains available when WS is blocked.
      return
    }
    this.socket = socket
    socket.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(String(event.data)) as {
          type?: string
          data?: { prompt_id?: string; node?: string | null; value?: number; max?: number }
        }
        const promptId = message.data?.prompt_id
        if (!promptId) return
        this.socketListeners.get(promptId)?.forEach((listener) => listener(message))
      } catch {
        // Non-JSON preview frames are expected.
      }
    })
    socket.addEventListener('close', () => {
      if (this.socket === socket) this.socket = null
      if (this.socketListeners.size) {
        window.clearTimeout(this.reconnectTimer)
        this.reconnectTimer = window.setTimeout(() => this.ensureSocket(), 2000)
      }
    })
  }

  private addSocketListener(
    promptId: string,
    listener: (message: {
      type?: string
      data?: { prompt_id?: string; node?: string | null; value?: number; max?: number }
    }) => void,
  ): () => void {
    const listeners = this.socketListeners.get(promptId) ?? new Set()
    listeners.add(listener)
    this.socketListeners.set(promptId, listeners)
    this.ensureSocket()
    return () => {
      listeners.delete(listener)
      if (!listeners.size) this.socketListeners.delete(promptId)
      if (!this.socketListeners.size) {
        window.clearTimeout(this.reconnectTimer)
        this.socket?.close()
        this.socket = null
      }
    }
  }

  private headers(extra?: HeadersInit): Headers {
    const headers = new Headers(extra)
    if (this.apiKey) headers.set('Authorization', `Bearer ${this.apiKey}`)
    return headers
  }

  private async request(path: string, init: RequestInit = {}, timeoutMs = 15_000): Promise<Response> {
    if (!this.serverUrl) throw new Error('Server URL が未入力です。')
    const response = await withTimeout(fetch(`${this.serverUrl}${path}`, {
      ...init,
      credentials: 'include',
      headers: this.headers(init.headers),
    }), timeoutMs)
    if (!response.ok) {
      const detail = await response.text()
      throw new Error(`${response.status} ${response.statusText}${detail ? `\n${detail}` : ''}`)
    }
    return response
  }

  async testConnection(timeoutMs = 15_000): Promise<void> {
    const controller = new AbortController()
    const timer = window.setTimeout(() => controller.abort(), timeoutMs)
    try {
      await this.request('/system_stats', { signal: controller.signal }, timeoutMs)
    } catch (error) {
      if (controller.signal.aborted) throw new Error('Request timed out')
      throw error
    } finally {
      window.clearTimeout(timer)
    }
  }

  private async modelList(nodeType: string, inputName: string): Promise<string[]> {
    try {
      const response = await this.request(`/object_info/${encodeURIComponent(nodeType)}`)
      const data = await response.json() as Record<string, {
        input?: { required?: Record<string, [unknown[]]> }
      }>
      const items = data[nodeType]?.input?.required?.[inputName]?.[0]
      return Array.isArray(items) ? items.map(String).sort((a, b) => a.localeCompare(b)) : []
    } catch {
      // Optional custom nodes (for example RIFE) may not exist on every Pod.
      return []
    }
  }

  private async folderModelList(folderName: string): Promise<string[]> {
    try {
      const response = await this.request(`/models/${encodeURIComponent(folderName)}`)
      const data = await response.json() as unknown
      return Array.isArray(data) ? data.map(String).sort((a, b) => a.localeCompare(b)) : []
    } catch {
      // Older ComfyUI builds may not expose /models/{folder}; object_info remains available.
      return []
    }
  }

  private async upscaleModelList(): Promise<string[]> {
    const [nodeModels, folderModels] = await Promise.all([
      this.modelList('UpscaleModelLoader', 'model_name'),
      this.folderModelList('upscale_models'),
    ])
    return [...new Set([...nodeModels, ...folderModels])].sort((a, b) => a.localeCompare(b))
  }

  private async hasNode(nodeType: string): Promise<boolean> {
    try {
      const response = await this.request(`/object_info/${encodeURIComponent(nodeType)}`)
      const data = await response.json() as Record<string, unknown>
      return Boolean(data[nodeType])
    } catch {
      return false
    }
  }

  async getModelLists(): Promise<ModelLists> {
    const nodeTypes = ['VHS_LoadVideo', 'VHS_LoadVideoFFmpeg', 'UpscaleModelLoader', 'ImageUpscaleWithModel', 'ImageScaleBy', 'RIFE VFI', 'DownloadAndLoadGIMMVFIModel', 'GIMMVFI_interpolate', 'VHS_VideoCombine', 'FFmpegVideoMerge_Pro', 'MugenVideoPathLoader', 'MugenFFmpegConcat']
    const [unet, clip, vae, clipVision, lora, rife, gimm, samplers, schedulers, upscale, nodeSupport] = await Promise.all([
      this.modelList('UNETLoader', 'unet_name'),
      this.modelList('CLIPLoader', 'clip_name'),
      this.modelList('VAELoader', 'vae_name'),
      this.modelList('CLIPVisionLoader', 'clip_name'),
      this.modelList('LoraLoaderModelOnly', 'lora_name'),
      this.modelList('RIFE VFI', 'ckpt_name'),
      this.modelList('DownloadAndLoadGIMMVFIModel', 'model'),
      this.modelList('KSamplerAdvanced', 'sampler_name'),
      this.modelList('KSamplerAdvanced', 'scheduler'),
      this.upscaleModelList(),
      Promise.all(nodeTypes.map(async (nodeType) => ({ nodeType, supported: await this.hasNode(nodeType) }))),
    ])
    return {
      unet, clip, vae, clipVision, lora: ['None', ...lora], rife, gimm, samplers, schedulers, upscale,
      nodes: nodeSupport.filter((item) => item.supported).map((item) => item.nodeType),
    }
  }

  async uploadInput(file: File): Promise<string> {
    const form = new FormData()
    form.append('image', file, file.name)
    let lastError: unknown
    for (let attempt = 0; attempt < 3; attempt += 1) {
      if (attempt) await new Promise((resolve) => window.setTimeout(resolve, attempt * 2000))
      try {
        const response = await this.request('/upload/image', { method: 'POST', body: form }, 310_000)
        const data = await response.json() as { name?: string }
        if (!data.name) throw new Error('Upload response did not contain a filename.')
        return data.name
      } catch (error) {
        lastError = error
      }
    }
    throw lastError
  }

  async uploadImage(file: File): Promise<string> {
    return await this.uploadInput(file)
  }

  async submitWorkflow(workflow: Workflow, metadata?: PromptSubmissionMetadata): Promise<string> {
    const response = await this.request('/prompt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: workflow,
        client_id: this.clientId,
        ...(metadata ? { extra_data: { mugen: metadata } } : {}),
      }),
    }, 35_000)
    const data = await response.json() as { prompt_id?: string }
    if (!data.prompt_id) throw new Error('ComfyUI response did not contain prompt_id.')
    return data.prompt_id
  }

  async getHistory(promptId?: string): Promise<ComfyHistory> {
    const path = promptId ? `/history/${encodeURIComponent(promptId)}` : '/history?max_items=20'
    const response = await this.request(path)
    return await response.json() as ComfyHistory
  }

  async getQueue(): Promise<QueueResponse> {
    const response = await this.request('/queue')
    return await response.json() as QueueResponse
  }

  async getQueueSnapshot(): Promise<{ running: QueuePromptSummary[]; pending: QueuePromptSummary[] }> {
    const queue = await this.getQueue()
    const prompts = (rows: unknown[][] | undefined) => (rows ?? [])
      .map(queuePrompt)
      .filter((item): item is QueuePromptSummary => Boolean(item))
    return { running: prompts(queue.queue_running), pending: prompts(queue.queue_pending) }
  }

  async getQueuePromptIds(): Promise<{ running: string[]; pending: string[] }> {
    const queue = await this.getQueueSnapshot()
    return {
      running: queue.running.map((item) => item.promptId),
      pending: queue.pending.map((item) => item.promptId),
    }
  }

  async cancelPrompt(promptId: string): Promise<'deleted' | 'interrupted'> {
    const queue = await this.getQueue()
    const ids = (rows: unknown[][] | undefined) => (rows ?? [])
      .map((row) => typeof row?.[1] === 'string' ? row[1] : '')
      .filter(Boolean)
    if (ids(queue.queue_pending).includes(promptId)) {
      await this.request('/queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delete: [promptId] }),
      })
      return 'deleted'
    }
    if (ids(queue.queue_running).includes(promptId)) {
      await this.request('/interrupt', { method: 'POST' })
      return 'interrupted'
    }
    throw new Error('Job は ComfyUI の実行中・待機中キューに見つかりません。')
  }

  async recoverPromptIds(): Promise<{ running: string[]; pending: string[]; history: ComfyHistory }> {
    const [queue, history] = await Promise.all([this.getQueuePromptIds(), this.getHistory()])
    return { ...queue, history }
  }

  async monitorPrompt(
    promptId: string,
    onProgress: (update: ProgressUpdate) => void,
    signal?: AbortSignal,
  ): Promise<ComfyHistory> {
    return await new Promise<ComfyHistory>((resolve, reject) => {
      let settled = false
      const cleanup = () => {
        window.clearInterval(pollTimer)
        removeSocketListener()
        signal?.removeEventListener('abort', abort)
      }
      const finish = (history: ComfyHistory) => {
        if (settled) return
        settled = true
        cleanup()
        resolve(history)
      }
      const fail = (error: unknown) => {
        if (settled) return
        settled = true
        cleanup()
        reject(error instanceof Error ? error : new Error(String(error)))
      }
      const checkHistory = async () => {
        try {
          const history = await this.getHistory(promptId)
          const entry = history[promptId]
          const status = entry?.status && typeof entry.status === 'object'
            ? entry.status as { status_str?: unknown; messages?: unknown }
            : null
          if (status?.status_str === 'error') {
            fail(new Error(formatComfyExecutionError(status.messages ?? status)))
          } else if (entry?.outputs && Object.keys(entry.outputs).length) {
            finish(history)
          }
        } catch {
          // WebSocket remains the primary channel; transient polling errors are ignored.
        }
      }
      const abort = () => fail(new DOMException('Aborted', 'AbortError'))
      const pollTimer = window.setInterval(checkHistory, 5000)
      signal?.addEventListener('abort', abort, { once: true })
      const removeSocketListener = this.addSocketListener(promptId, (message) => {
        if (message.type === 'progress') {
          onProgress({ stepValue: message.data?.value ?? 0, stepMax: Math.max(1, message.data?.max ?? 1) })
        } else if (message.type === 'executing') {
          if (message.data?.node === null) void checkHistory()
          else onProgress({ currentNode: message.data?.node ?? '' })
        }
      })
      void checkHistory()
    })
  }

  extractMedia(history: ComfyHistory, promptId: string): MediaDescriptor | undefined {
    const outputs = history[promptId]?.outputs ?? {}
    for (const output of Object.values(outputs).reverse()) {
      const video = output.videos?.[0] ?? output.gifs?.[0]
      if (video) return { filename: video.filename, subfolder: video.subfolder ?? '', type: video.type ?? 'output', kind: 'video' }
      if (output.video?.[0]) return { filename: output.video[0], subfolder: output.video[1] ?? '', type: output.video[2] ?? 'output', kind: 'video' }
      const image = output.images?.[0]
      if (image) return { filename: image.filename, subfolder: image.subfolder ?? '', type: image.type ?? 'output', kind: 'image' }
    }
    return undefined
  }

  viewUrl(media: MediaDescriptor): string {
    const params = new URLSearchParams({
      filename: media.filename,
      subfolder: media.subfolder ?? '',
      type: media.type ?? 'output',
    })
    return `${this.serverUrl}/view?${params}`
  }

  async playableUrl(media: MediaDescriptor): Promise<string> {
    if (!this.apiKey) return this.viewUrl(media)
    const response = await this.request(`/view?${new URLSearchParams({
      filename: media.filename,
      subfolder: media.subfolder ?? '',
      type: media.type ?? 'output',
    })}`, {}, 310_000)
    return URL.createObjectURL(await response.blob())
  }

  async download(media: MediaDescriptor): Promise<void> {
    const response = await this.request(`/view?${new URLSearchParams({
      filename: media.filename,
      subfolder: media.subfolder ?? '',
      type: media.type ?? 'output',
    })}`, {}, 310_000)
    const objectUrl = URL.createObjectURL(await response.blob())
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = media.filename
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }
}
